import React, { useEffect, useState, useRef } from "react";
import { api, API } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Play, Pause, Voicemail as VoicemailIcon, Sparkles, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Voicemails() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [playingId, setPlayingId] = useState(null);
    const [transcribingId, setTranscribingId] = useState(null);
    const audioRefs = useRef({});

    const load = async () => {
        try {
            const { data } = await api.get("/voicemails");
            setItems(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const togglePlay = async (vm) => {
        const a = audioRefs.current[vm.voicemail_id];
        if (!a) return;
        if (playingId === vm.voicemail_id) {
            a.pause();
            setPlayingId(null);
        } else {
            Object.values(audioRefs.current).forEach((el) => el?.pause());
            try {
                await a.play();
                setPlayingId(vm.voicemail_id);
            } catch (e) {
                toast.error("Cannot play this recording");
            }
            if (!vm.is_read) {
                api.post(`/voicemails/${vm.voicemail_id}/read`).then(load);
            }
        }
    };

    const seedDemo = async () => {
        try {
            await api.post("/voicemails/demo");
            toast.success("Demo voicemail added");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const transcribe = async (vm) => {
        setTranscribingId(vm.voicemail_id);
        try {
            const { data } = await api.post(`/voicemails/${vm.voicemail_id}/transcribe`);
            setItems((prev) => prev.map((v) => v.voicemail_id === vm.voicemail_id ? { ...v, transcript: data.transcript } : v));
            toast.success("Transcribed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Transcription failed");
        } finally {
            setTranscribingId(null);
        }
    };

    return (
        <div className="space-y-8" data-testid="voicemails-page">
            <div className="flex items-end justify-between">
                <div>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2">— Voicemail</p>
                    <h1 className="font-display text-4xl font-light tracking-tight">
                        Messages you <span className="font-serif-italic">missed.</span>
                    </h1>
                </div>
                <Button
                    onClick={seedDemo}
                    data-testid="seed-demo-vm-btn"
                    variant="outline"
                    className="rounded-sm border-[#DDD6FE] h-10"
                >
                    <Sparkles className="w-4 h-4 mr-2" /> Add demo
                </Button>
            </div>

            {loading && <div className="text-sm text-zinc-500">Loading…</div>}

            {!loading && items.length === 0 && (
                <div className="border border-[#DDD6FE] rounded-sm p-12 text-center bg-paper">
                    <VoicemailIcon className="w-6 h-6 mx-auto text-emerald-deep mb-3" />
                    <p className="font-display text-xl">No voicemails yet</p>
                    <p className="text-zinc-600 mt-2">When someone leaves a voicemail on your number, it shows here.</p>
                </div>
            )}

            <div className="grid gap-px bg-[#DDD6FE] border border-[#DDD6FE]">
                {items.map((vm) => {
                    const audioUrl = vm.storage_path ? `${API}/api/media/${vm.storage_path}` : null;
                    return (
                        <div
                            key={vm.voicemail_id}
                            className={`bg-paper p-5 ${vm.is_read ? "" : "border-l-4 border-l-emerald-deep"}`}
                            data-testid={`vm-${vm.voicemail_id}`}
                        >
                            <div className="flex items-center gap-5">
                                <button
                                    disabled={!audioUrl}
                                    onClick={() => togglePlay(vm)}
                                    data-testid={`vm-play-${vm.voicemail_id}`}
                                    className="w-11 h-11 rounded-sm bg-espresso text-[#FAF5FF] grid place-items-center hover:bg-black disabled:opacity-40 flex-shrink-0"
                                >
                                    {playingId === vm.voicemail_id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                                </button>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-sm">{vm.caller || "Unknown"}</span>
                                        {vm.demo && (
                                            <span className="font-mono text-[9px] bg-[#E4DAF7] text-[#4C1D95] px-1.5 py-0.5 rounded-sm uppercase">
                                                Demo
                                            </span>
                                        )}
                                        {!vm.is_read && (
                                            <span className="font-mono text-[9px] text-emerald-deep border border-emerald-deep px-1.5 py-0.5 rounded-sm uppercase">
                                                New
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                                        {new Date(vm.created_at).toLocaleString()} · {formatDur(vm.duration_seconds)}
                                    </div>
                                    {audioUrl && (
                                        <audio
                                            ref={(el) => (audioRefs.current[vm.voicemail_id] = el)}
                                            src={audioUrl}
                                            onEnded={() => setPlayingId(null)}
                                            className="hidden"
                                        />
                                    )}
                                </div>
                                {!vm.transcript && (
                                    <Button
                                        onClick={() => transcribe(vm)}
                                        disabled={transcribingId === vm.voicemail_id}
                                        data-testid={`vm-transcribe-${vm.voicemail_id}`}
                                        variant="outline"
                                        className="rounded-sm border-[#DDD6FE] h-9 px-3"
                                    >
                                        {transcribingId === vm.voicemail_id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <>
                                                <FileText className="w-3.5 h-3.5 mr-2" /> Transcribe
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                            {vm.transcript && (
                                <div className="mt-4 ml-16 pl-5 border-l-2 border-emerald-deep" data-testid={`vm-transcript-${vm.voicemail_id}`}>
                                    <p className="font-mono text-[9px] uppercase text-emerald-deep tracking-wider mb-1.5">
                                        — Transcript
                                    </p>
                                    <p className="font-serif-italic text-base text-zinc-800 leading-relaxed">
                                        “{vm.transcript}”
                                    </p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function formatDur(s) {
    if (!s) return "—";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
}
